import {
  type AdminUpdateFeedbackRequest,
  type AgentFeedbackRecord,
  type AgentThreadRecord,
  FeedbackCategory,
  FeedbackSentiment,
} from "@/shared/domain/agentThread";
import { HttpClient, PaginatedResponse } from "@/shared/http";

import { ReconstructedPrompt, SessionDebugSummary } from "./ChatDebugRecord";
import { MigrationRecord, PerActionStats } from "./MigrationRecord";

interface Options {
  abortSignal?: AbortSignal;
}

interface GetPerActionStatsOptions extends Options {
  startTime: number; // Unix timestamp in seconds
  endTime: number; // Unix timestamp in seconds
  limit?: number;
}

interface GetAdminAgentThreadsOptions extends Options {
  orgId?: string;
  createdBy?: string;
  sessionId?: string;
  hasErrors?: boolean;
  pageToken?: string;
  limit?: number;
}

export interface GetAdminFeedbackOptions extends Options {
  sentiment?: FeedbackSentiment;
  category?: FeedbackCategory;
  orgId?: string;
  sessionId?: string;
  createdBy?: string;
  resolved?: boolean;
  hasNotes?: boolean;
  hasErrors?: boolean;
  createdAfter?: string; // ISO datetime
  createdBefore?: string; // ISO datetime
  pageToken?: string;
  limit?: number;
}

export interface FeedbackCountsBySession {
  [sessionId: string]: { positive: number; negative: number };
}

/**
 * Backfill the canonical ``thread_id`` / ``forked_from_thread_id`` fields
 * from their legacy ``session_id`` / ``forked_from_session_id`` siblings
 * when the wire response carries the pre-v2026_05_20 shape. Lossless on
 * already-canonical payloads.
 *
 * Defensive on the read side because admin /chats has been observed
 * returning the legacy shape post-deploy even though its route does not
 * register the ``AgentThreadRenameTransform``. Until that backend path is
 * pinned down, normalizing here keeps the table navigable.
 */
function normalizeThreadShape(record: AgentThreadRecord): AgentThreadRecord {
  const legacy = record as AgentThreadRecord & {
    session_id?: string;
    forked_from_session_id?: string | null;
  };
  if (record.thread_id !== undefined && record.thread_id !== null) {
    return record;
  }
  if (legacy.session_id === undefined) {
    return record;
  }
  return {
    ...record,
    thread_id: legacy.session_id,
    forked_from_thread_id:
      record.forked_from_thread_id ?? legacy.forked_from_session_id ?? null,
  };
}

export class AdminService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getMigrations(
    options?: Partial<Options>,
  ): Promise<MigrationRecord[]> {
    const requestUrl = this.#httpClient.constructUrl("v1/admin/migrations");

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return await response.json<MigrationRecord[]>();
  }

  public async getPerActionStats(
    options: GetPerActionStatsOptions,
  ): Promise<PerActionStats[]> {
    const searchParams = new URLSearchParams();
    searchParams.append("start_time", options.startTime.toString());
    searchParams.append("end_time", options.endTime.toString());

    if (options.limit !== undefined) {
      searchParams.append("limit", options.limit.toString());
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/admin/per-action-stats",
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return await response.json<PerActionStats[]>();
  }

  public async getAdminAgentThreads(
    options?: GetAdminAgentThreadsOptions,
  ): Promise<PaginatedResponse<AgentThreadRecord>> {
    const searchParams = new URLSearchParams();

    if (options?.orgId) {
      searchParams.append("org_id", options.orgId);
    }
    if (options?.createdBy) {
      searchParams.append("created_by", options.createdBy);
    }
    if (options?.sessionId) {
      searchParams.append("thread_id", options.sessionId);
    }
    if (options?.hasErrors !== undefined) {
      searchParams.append("has_errors", options.hasErrors.toString());
    }
    if (options?.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }
    if (options?.limit !== undefined) {
      searchParams.append("limit", options.limit.toString());
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/admin/chats",
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    // The wire response can arrive shaped with the legacy ``session_id``
    // (and ``forked_from_session_id``) fields when the API version transform
    // fires — observed on deployed lambdas serving admin /chats even though
    // the route doesn't register the transform locally. Normalize before
    // handing the rows to React: downstream code only knows ``thread_id``.
    const raw = await response.json<PaginatedResponse<AgentThreadRecord>>();
    return {
      ...raw,
      items: raw.items.map(normalizeThreadShape),
    };
  }

  public async getAdminFeedback(
    options?: GetAdminFeedbackOptions,
  ): Promise<PaginatedResponse<AgentFeedbackRecord>> {
    const searchParams = new URLSearchParams();

    if (options?.sentiment) {
      searchParams.append("sentiment", options.sentiment);
    }
    if (options?.category) {
      searchParams.append("category", options.category);
    }
    if (options?.orgId) {
      searchParams.append("org_id", options.orgId);
    }
    if (options?.sessionId) {
      searchParams.append("thread_id", options.sessionId);
    }
    if (options?.createdBy) {
      searchParams.append("created_by", options.createdBy);
    }
    if (options?.resolved !== undefined) {
      searchParams.append("resolved", options.resolved.toString());
    }
    if (options?.hasNotes !== undefined) {
      searchParams.append("has_notes", options.hasNotes.toString());
    }
    if (options?.hasErrors !== undefined) {
      searchParams.append("has_errors", options.hasErrors.toString());
    }
    if (options?.createdAfter) {
      searchParams.append("created_after", options.createdAfter);
    }
    if (options?.createdBefore) {
      searchParams.append("created_before", options.createdBefore);
    }
    if (options?.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }
    if (options?.limit !== undefined) {
      searchParams.append("limit", options.limit.toString());
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/admin/feedback",
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return response.json<PaginatedResponse<AgentFeedbackRecord>>();
  }

  public async updateAdminFeedback(
    feedbackId: string,
    request: AdminUpdateFeedbackRequest,
    options?: Options,
  ): Promise<AgentFeedbackRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/admin/feedback/${feedbackId}`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });
    return response.json<AgentFeedbackRecord>();
  }

  public async getAdminFeedbackById(
    feedbackId: string,
    options?: Options,
  ): Promise<AgentFeedbackRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/admin/feedback/${feedbackId}`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<AgentFeedbackRecord>();
  }

  public async getChatDebugSummary(
    chatId: string,
    options?: Options,
  ): Promise<SessionDebugSummary> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/admin/chats/${chatId}/debug`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<SessionDebugSummary>();
  }

  public async getChatMessagePrompt(
    chatId: string,
    messageSequenceNum: number,
    options?: Options,
  ): Promise<ReconstructedPrompt> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/admin/chats/${chatId}/messages/${messageSequenceNum}/prompt`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<ReconstructedPrompt>();
  }

  public async getAdminFeedbackCountsForAgentThreads(
    filters: {
      orgId?: string;
      createdBy?: string;
      sessionId?: string;
    },
    options?: Options,
  ): Promise<FeedbackCountsBySession> {
    // Backend rejects unrestricted scans (no filter) with a 400. Throw here
    // rather than silently returning ``{}``: an empty-object response would
    // mask both an honest "no rows match" and the misuse-of-the-API case
    // where the caller forgot to gate this call. Hook callers
    // (``useAdminFeedbackCountsForAgentThreads``) already pin ``enabled`` on the
    // same predicate, so this throw doesn't fire on the happy path.
    if (!filters.orgId && !filters.createdBy && !filters.sessionId) {
      throw new Error(
        "getAdminFeedbackCountsForAgentThreads requires at least one of orgId, createdBy, or sessionId.",
      );
    }

    const searchParams = new URLSearchParams();
    if (filters.orgId) {
      searchParams.append("org_id", filters.orgId);
    }
    if (filters.createdBy) {
      searchParams.append("created_by", filters.createdBy);
    }
    if (filters.sessionId) {
      searchParams.append("thread_id", filters.sessionId);
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/admin/chats/feedback-counts",
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });

    return response.json<FeedbackCountsBySession>();
  }
}
